import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_ORDER_NUMBERING,
  ORDER_YEAR_STYLES,
  WATERMARK_POSITIONS,
  formatConfiguredOrderNumber,
  type OrderYearStyle,
  type WatermarkPosition,
} from '@tailoring-crm/shared';
import { BillDesignCard } from '../components/invoices/bill-design-card.js';
import { PageHeader } from '../components/layout/page-header.js';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  ListSkeleton,
  useToast,
} from '@tailoring-crm/ui';
import { isApiRequestError } from '../lib/api/errors.js';
import { useAuth } from '../lib/auth/auth-context.js';
import {
  usePresentationSettings,
  useUpdateShopSettings,
} from '../lib/settings/use-shop-settings.js';

/**
 * How the shop presents itself: where to pay, how its orders are numbered, what mark goes on
 * its photographs, and what its bills look like.
 *
 * FOUR CARDS THAT SAVE SEPARATELY. `PATCH /settings` merges what it is sent, so each card
 * carries only its own section — which means a stale copy of the bank details cannot overwrite
 * a watermark somebody changed in another tab, and each Save button means what it says.
 *
 * Every one of them shows its own RESULT rather than only its inputs: the order-numbering card
 * renders the next number as you type, the watermark card renders the mark, and the bill design
 * card renders a sample bill. A shop
 * setting a format it will print on a thousand tags should see the format, not describe it.
 */
export function ShopPresentationPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const settings = usePresentationSettings();

  // The API refuses `settings.write` to anybody but the owner; so does this.
  const canWrite = role === 'owner';

  if (settings.isPending) return <ListSkeleton rows={5} />;

  return (
    <>
      <PageHeader title={t('presentation.title')} description={t('presentation.description')} />

      <div className="flex flex-col gap-4">
        <BankCard canWrite={canWrite} />
        <OrderNumberingCard canWrite={canWrite} />
        <WatermarkCard canWrite={canWrite} />
        <BillDesignCard canWrite={canWrite} />
      </div>
    </>
  );
}

/* ---------------------------------------------------------------------------------------- */

function BankCard({ canWrite }: { canWrite: boolean }): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const settings = usePresentationSettings();
  const update = useUpdateShopSettings();
  const stored = settings.data?.bank;

  const [form, setForm] = useState({
    accountName: '',
    bankName: '',
    branch: '',
    accountNumber: '',
    ifsc: '',
    upiId: '',
    showOnInvoice: false,
  });

  useEffect(() => {
    if (!stored) return;
    setForm({
      accountName: stored.accountName ?? '',
      bankName: stored.bankName ?? '',
      branch: stored.branch ?? '',
      accountNumber: stored.accountNumber ?? '',
      ifsc: stored.ifsc ?? '',
      upiId: stored.upiId ?? '',
      showOnInvoice: stored.showOnInvoice,
    });
  }, [stored]);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void (async () => {
      try {
        await update.mutateAsync({
          bank: {
            accountName: form.accountName.trim() || null,
            bankName: form.bankName.trim() || null,
            branch: form.branch.trim() || null,
            accountNumber: form.accountNumber.trim() || null,
            ifsc: form.ifsc.trim() || null,
            upiId: form.upiId.trim() || null,
            showOnInvoice: form.showOnInvoice,
          },
        });
        toast({ title: t('presentation.saved'), variant: 'success' });
      } catch (caught) {
        toast({
          title: isApiRequestError(caught) ? caught.message : t('error.unexpected'),
          variant: 'destructive',
        });
      }
    })();
  };

  const field = (key: keyof typeof form, label: string): ReactElement => (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`bank-${key}`}>{label}</Label>
      <Input
        id={`bank-${key}`}
        value={String(form[key])}
        disabled={!canWrite}
        onChange={(event) => setForm((previous) => ({ ...previous, [key]: event.target.value }))}
      />
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('presentation.bank.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-3" onSubmit={submit}>
          <p className="text-sm text-muted-foreground">{t('presentation.bank.hint')}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {field('accountName', t('presentation.bank.accountName'))}
            {field('bankName', t('presentation.bank.bankName'))}
            {field('branch', t('presentation.bank.branch'))}
            {field('accountNumber', t('presentation.bank.accountNumber'))}
            {field('ifsc', t('presentation.bank.ifsc'))}
            {field('upiId', t('presentation.bank.upiId'))}
          </div>

          {/*
            Filling the section in and PRINTING it are two decisions. A shop that records its
            account for its own reference has not agreed to put it on every bill it hands over.
          */}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.showOnInvoice}
              disabled={!canWrite}
              onChange={(event) =>
                setForm((previous) => ({ ...previous, showOnInvoice: event.target.checked }))
              }
            />
            {t('presentation.bank.showOnInvoice')}
          </label>

          {canWrite ? (
            <div>
              <Button type="submit" disabled={update.isPending}>
                {t('common.save')}
              </Button>
            </div>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------------------------------------------------- */

function OrderNumberingCard({ canWrite }: { canWrite: boolean }): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const settings = usePresentationSettings();
  const update = useUpdateShopSettings();
  const stored = settings.data?.orderNumbering ?? DEFAULT_ORDER_NUMBERING;

  const [prefix, setPrefix] = useState(stored.prefix);
  const [yearStyle, setYearStyle] = useState<OrderYearStyle>(stored.yearStyle);
  const [padding, setPadding] = useState(String(stored.padding));

  useEffect(() => {
    setPrefix(stored.prefix);
    setYearStyle(stored.yearStyle);
    setPadding(String(stored.padding));
  }, [stored.prefix, stored.yearStyle, stored.padding]);

  /**
   * What the next order will actually be called, built by the SAME function the server uses.
   * A preview computed by a different code path is a preview that can be wrong, and this one
   * is a format a shop is about to write on a thousand tags.
   */
  const preview = formatConfiguredOrderNumber(
    { prefix: prefix || 'ORD', yearStyle, padding: Number(padding) || 4 },
    '2026-27',
    1,
  );

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void (async () => {
      try {
        await update.mutateAsync({
          orderNumbering: { prefix, yearStyle, padding: Number(padding) || 4 },
        });
        toast({ title: t('presentation.saved'), variant: 'success' });
      } catch (caught) {
        toast({
          title: isApiRequestError(caught) ? caught.message : t('error.unexpected'),
          variant: 'destructive',
        });
      }
    })();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('presentation.numbering.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-3" onSubmit={submit}>
          <p className="text-sm text-muted-foreground">{t('presentation.numbering.hint')}</p>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="numbering-prefix">{t('presentation.numbering.prefix')}</Label>
              <Input
                id="numbering-prefix"
                value={prefix}
                maxLength={10}
                disabled={!canWrite}
                onChange={(event) => setPrefix(event.target.value.toUpperCase())}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="numbering-year">{t('presentation.numbering.yearStyle')}</Label>
              <select
                id="numbering-year"
                value={yearStyle}
                disabled={!canWrite}
                onChange={(event) => setYearStyle(event.target.value as OrderYearStyle)}
                className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
              >
                {ORDER_YEAR_STYLES.map((one) => (
                  <option key={one} value={one}>
                    {t(`presentation.numbering.year.${one}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="numbering-padding">{t('presentation.numbering.padding')}</Label>
              <Input
                id="numbering-padding"
                inputMode="numeric"
                value={padding}
                disabled={!canWrite}
                onChange={(event) => setPadding(event.target.value)}
              />
            </div>
          </div>

          <p className="rounded-md bg-muted p-3 text-sm">
            {t('presentation.numbering.preview')}{' '}
            <span className="font-mono font-semibold">{preview}</span>
          </p>

          {canWrite ? (
            <div>
              <Button type="submit" disabled={update.isPending}>
                {t('common.save')}
              </Button>
            </div>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------------------------------------------------- */

function WatermarkCard({ canWrite }: { canWrite: boolean }): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const settings = usePresentationSettings();
  const update = useUpdateShopSettings();
  const stored = settings.data?.watermark;

  const [enabled, setEnabled] = useState(false);
  const [text, setText] = useState('');
  const [position, setPosition] = useState<WatermarkPosition>('bottom_right');
  const [opacity, setOpacity] = useState(60);

  useEffect(() => {
    if (!stored) return;
    setEnabled(stored.enabled);
    // Defaulted to the shop's own name so the box is never empty in front of somebody who has
    // just switched it on — but the STORED value stays null until they save, so nothing is
    // stamped on a photograph by a checkbox alone.
    setText(stored.text ?? settings.data?.shopName ?? '');
    setPosition(stored.position);
    setOpacity(stored.opacity);
  }, [stored, settings.data?.shopName]);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void (async () => {
      try {
        await update.mutateAsync({
          watermark: { enabled, text: text.trim() || null, position, opacity },
        });
        toast({ title: t('presentation.saved'), variant: 'success' });
      } catch (caught) {
        toast({
          title: isApiRequestError(caught) ? caught.message : t('error.unexpected'),
          variant: 'destructive',
        });
      }
    })();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('presentation.watermark.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-3" onSubmit={submit}>
          <p className="text-sm text-muted-foreground">{t('presentation.watermark.hint')}</p>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              disabled={!canWrite}
              onChange={(event) => setEnabled(event.target.checked)}
            />
            {t('presentation.watermark.enabled')}
          </label>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="watermark-text">{t('presentation.watermark.text')}</Label>
              <Input
                id="watermark-text"
                value={text}
                maxLength={60}
                disabled={!canWrite}
                onChange={(event) => setText(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="watermark-position">{t('presentation.watermark.position')}</Label>
              <select
                id="watermark-position"
                value={position}
                disabled={!canWrite}
                onChange={(event) => setPosition(event.target.value as WatermarkPosition)}
                className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
              >
                {WATERMARK_POSITIONS.map((one) => (
                  <option key={one} value={one}>
                    {t(`presentation.watermark.at.${one}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="watermark-opacity">
                {t('presentation.watermark.opacity', { value: opacity })}
              </Label>
              <input
                id="watermark-opacity"
                type="range"
                min={10}
                max={100}
                step={5}
                value={opacity}
                disabled={!canWrite}
                onChange={(event) => setOpacity(Number(event.target.value))}
              />
            </div>
          </div>

          {/* The mark itself, over a stand-in photograph. Describing a watermark is not the
              same as seeing one, and this is a decision about how something looks. */}
          <div className="relative h-32 overflow-hidden rounded-md bg-gradient-to-br from-muted to-accent">
            {enabled && text.trim() ? (
              <span
                aria-label={t('presentation.watermark.previewLabel')}
                className={`absolute font-semibold text-white drop-shadow ${
                  position === 'centre'
                    ? 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
                    : position === 'top_left'
                      ? 'left-3 top-3'
                      : position === 'top_right'
                        ? 'right-3 top-3'
                        : position === 'bottom_left'
                          ? 'bottom-3 left-3'
                          : 'bottom-3 right-3'
                }`}
                style={{ opacity: opacity / 100 }}
              >
                {text}
              </span>
            ) : null}
          </div>

          {canWrite ? (
            <div>
              <Button type="submit" disabled={update.isPending}>
                {t('common.save')}
              </Button>
            </div>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}

export default ShopPresentationPage;
